import React, { useEffect, useMemo, useState } from "react";
import { IndustryDataTopbar } from "./IndustryDataTopbar";
import {
  IndustryDataService,
  ResidentialMortgageIndustryResponse,
  IndustryDataset,
  IndustryDatasetRow,
} from "../services/industryDataService";

type IndustryView =
  | "overview"
  | "explorer"
  | "trends"
  | "comparisons"
  | "methodology"
  | "access";

interface IndustryDataProps {
  onSidebarToggle: () => void;
}

type SortState = {
  column: string | null;
  direction: "asc" | "desc";
};

type TimeSeriesPoint = { date: string; value: number };
type TimeSeries = { label: string; color: string; points: TimeSeriesPoint[] };

const CYBER_COLORS = [
  "rgba(0, 212, 255, 0.95)", // cyber blue
  "rgba(139, 92, 246, 0.95)", // blockchain purple
  "rgba(0, 255, 136, 0.95)", // neon green
  "rgba(255, 184, 0, 0.95)", // amber
  "rgba(255, 51, 102, 0.95)", // red
  "rgba(255, 255, 255, 0.9)", // white
];

const panelClass =
  "relative p-4 pb-4.5 rounded-[18px] bg-gradient-to-br from-[#22284d] to-[#10142f] border border-[rgba(139,146,176,0.2)] shadow-[0_16px_40px_rgba(0,0,0,0.6)] overflow-hidden";

const innerPanelClass =
  "rounded-xl bg-gradient-to-b from-[#1f2849] to-[#050716] border border-[rgba(139,146,176,0.35)]";

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "").trim();
    if (!cleaned) return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function downloadTextFile(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function formatDateDisplay(dateStr: string): string {
  // API already normalizes to YYYY-MM-DD. Keep it stable.
  return dateStr;
}

function getUniqueValues(rows: IndustryDatasetRow[], column: string): string[] {
  const set = new Set<string>();
  for (const row of rows) {
    const v = row[column];
    if (v === null || v === undefined) continue;
    set.add(String(v));
  }
  return Array.from(set).sort();
}

function isNumericColumn(dataset: IndustryDataset, column: string): boolean {
  const sample = dataset.rows.slice(0, 25);
  let hits = 0;
  let seen = 0;
  for (const row of sample) {
    if (!(column in row)) continue;
    const n = toNumber(row[column]);
    if (n === null) continue;
    seen += 1;
    hits += 1;
  }
  return seen > 0 && hits / seen >= 0.8;
}

function pickPrimaryValueColumn(
  dataset: IndustryDataset,
  dateColumn: string
): string | null {
  const numericCols = dataset.columns.filter(
    (c) => c !== dateColumn && isNumericColumn(dataset, c)
  );
  if (numericCols.length === 0) return null;
  if (numericCols.length === 1) return numericCols[0];

  const preferred = numericCols.find((c) => /\(#\)/i.test(c)) ||
    numericCols.find((c) => /value/i.test(c)) ||
    numericCols[0];
  return preferred;
}

function maxDateString(dates: string[]): string | null {
  if (dates.length === 0) return null;
  // YYYY-MM-DD lexicographic max works
  return [...dates].sort().at(-1) || null;
}

function sumForDate(
  dataset: IndustryDataset,
  dateColumn: string,
  valueColumn: string,
  date: string
): number {
  let total = 0;
  for (const row of dataset.rows) {
    if (String(row[dateColumn]) !== date) continue;
    const n = toNumber(row[valueColumn]);
    if (n !== null) total += n;
  }
  return total;
}

function sumByDate(
  dataset: IndustryDataset,
  dateColumn: string,
  valueColumn: string,
  rowFilter?: (row: IndustryDatasetRow) => boolean
): TimeSeriesPoint[] {
  const totals = new Map<string, number>();
  for (const row of dataset.rows) {
    if (rowFilter && !rowFilter(row)) continue;
    const d = String(row[dateColumn] ?? "");
    if (!d) continue;
    const n = toNumber(row[valueColumn]);
    if (n === null) continue;
    totals.set(d, (totals.get(d) || 0) + n);
  }
  return Array.from(totals.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, value]) => ({ date, value }));
}

function percentChange(current: number, prev: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(prev)) return null;
  if (prev === 0) return null;
  return ((current - prev) / prev) * 100;
}

const Badge: React.FC<{ value: string; tone?: "neutral" | "pos" | "neg" }> = ({
  value,
  tone = "neutral",
}) => {
  const klass =
    tone === "pos"
      ? "bg-[rgba(0,255,136,0.12)] text-neon-green"
      : tone === "neg"
      ? "bg-[rgba(255,51,102,0.12)] text-error-red"
      : "bg-[rgba(0,212,255,0.10)] text-cyber-blue";
  return (
    <span className={`inline-flex items-center gap-1.25 px-2 py-0.5 rounded-full text-[11px] ${klass}`}>
      {value}
    </span>
  );
};

const PillTabs: React.FC<{
  tabs: { id: IndustryView; label: string }[];
  active: IndustryView;
  onChange: (id: IndustryView) => void;
}> = ({ tabs, active, onChange }) => {
  return (
    <div className="inline-flex p-0.75 rounded-full bg-[rgba(7,9,26,0.9)] border border-[rgba(139,146,176,0.6)] text-[11px] gap-0.75 flex-wrap">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`border-none rounded-full px-2.5 py-1 bg-transparent cursor-pointer text-[11px] transition-all duration-200 ${
            active === t.id
              ? "bg-gradient-to-r from-[rgba(0,212,255,0.46)] to-[rgba(139,92,246,0.6)] text-white"
              : "text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)] hover:text-white"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
};

const Select: React.FC<{
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}> = ({ label, value, options, onChange }) => {
  return (
    <label className="flex flex-col gap-1 min-w-[200px]">
      <span className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0]">
        {label}
      </span>
      <select
        className="px-3 py-2 rounded-full border border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[12px] text-white outline-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
};

const SkeletonBlock: React.FC<{ className?: string }> = ({ className }) => (
  <div
    className={`animate-pulse bg-[rgba(255,255,255,0.08)] rounded-xl ${className || ""}`}
  />
);

const LineChart: React.FC<{
  title: string;
  subtitle?: string;
  series: TimeSeries[];
  height?: number;
}> = ({ title, subtitle, series, height = 230 }) => {
  const { dates, minY, maxY } = useMemo(() => {
    const dateSet = new Set<string>();
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    series.forEach((s) => {
      s.points.forEach((p) => {
        dateSet.add(p.date);
        min = Math.min(min, p.value);
        max = Math.max(max, p.value);
      });
    });
    const orderedDates = Array.from(dateSet).sort();
    if (!Number.isFinite(min)) min = 0;
    if (!Number.isFinite(max)) max = 1;
    if (min === max) max = min + 1;
    return { dates: orderedDates, minY: min, maxY: max };
  }, [series]);

  const paths = useMemo(() => {
    const dateIndex = new Map(dates.map((d, i) => [d, i]));
    const w = 100;
    const h = 60;
    const padTop = 8;
    const padBottom = 10;

    const yFor = (v: number) => {
      const t = (v - minY) / (maxY - minY);
      const usable = h - padTop - padBottom;
      return h - padBottom - t * usable;
    };

    const xFor = (d: string) => {
      const i = dateIndex.get(d) ?? 0;
      const denom = Math.max(dates.length - 1, 1);
      return (i / denom) * w;
    };

    const buildPath = (pts: TimeSeriesPoint[]) => {
      const ordered = [...pts].sort((a, b) => a.date.localeCompare(b.date));
      if (ordered.length === 0) return "";
      return ordered
        .map((p, idx) => {
          const x = xFor(p.date);
          const y = yFor(p.value);
          return `${idx === 0 ? "M" : "L"} ${x},${y}`;
        })
        .join(" ");
    };

    return series.map((s) => ({
      label: s.label,
      color: s.color,
      d: buildPath(s.points),
    }));
  }, [dates, maxY, minY, series]);

  const lastDate = dates.at(-1);
  const firstDate = dates.at(0);

  return (
    <article className={panelClass}>
      <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
            {title}
          </div>
          {subtitle && <div className="text-[11px] text-[#8b92b0]">{subtitle}</div>}
        </div>
        <div className="text-[11px] text-[#8b92b0]">
          {firstDate && lastDate ? (
            <span>
              Window: <strong>{formatDateDisplay(firstDate)}</strong> →{" "}
              <strong>{formatDateDisplay(lastDate)}</strong>
            </span>
          ) : (
            <span>Window: N/A</span>
          )}
        </div>
      </div>

      <div className={`mt-1.5 p-2.5 pb-3.5 relative overflow-hidden ${innerPanelClass}`}>
        <div className="flex items-center justify-between gap-2.5 mb-1.5 text-[11px] text-[#8b92b0] flex-wrap">
          <div className="flex items-center gap-2.5 flex-wrap">
            {series.map((s) => (
              <span key={s.label} className="inline-flex items-center gap-1.25">
                <span
                  className="w-2.25 h-2.25 rounded-full"
                  style={{ background: s.color }}
                />
                {s.label}
              </span>
            ))}
          </div>
          <div>
            Y-range:{" "}
            <strong>
              {minY.toLocaleString("en-US")} – {maxY.toLocaleString("en-US")}
            </strong>
          </div>
        </div>

        <div
          className="relative overflow-hidden rounded-[10px] animate-grid-on"
          style={{
            height,
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to right, rgba(255, 255, 255, 0.04) 1px, transparent 1px)`,
            backgroundSize: "0 0",
          }}
        >
          {series.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-[#8b92b0] text-sm">No data available</div>
            </div>
          ) : (
            <div className="absolute inset-0 pointer-events-none">
              <svg
                viewBox="0 0 100 60"
                preserveAspectRatio="none"
                className="w-full h-full"
              >
                {paths.map((p) =>
                  p.d ? (
                    <path
                      key={p.label}
                      d={p.d}
                      fill="none"
                      stroke={p.color}
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ) : null
                )}
              </svg>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export const IndustryData: React.FC<IndustryDataProps> = ({ onSidebarToggle }) => {
  const [view, setView] = useState<IndustryView>("overview");
  const [data, setData] = useState<ResidentialMortgageIndustryResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedDataset, setSelectedDataset] = useState<string>("");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<SortState>({ column: null, direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const tabs = useMemo(
    () => [
      { id: "overview" as const, label: "Overview" },
      { id: "explorer" as const, label: "Data Explorer" },
      { id: "trends" as const, label: "Trends" },
      { id: "comparisons" as const, label: "Comparisons" },
      { id: "methodology" as const, label: "Methodology" },
      { id: "access" as const, label: "Access & Downloads" },
    ],
    []
  );

  const metrics = data?.data.metrics || [];
  const dateColumn = data?.data.dateColumn || "Reporting Date";
  const datasets = data?.data.datasets || {};

  const currentDataset: IndustryDataset | null = useMemo(() => {
    if (!selectedDataset) return null;
    return datasets[selectedDataset] || null;
  }, [datasets, selectedDataset]);

  useEffect(() => {
    const fetchIndustryData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await IndustryDataService.getResidentialMortgageIndustryData();
        setData(res);
        const first = res.data.metrics?.[0] || Object.keys(res.data.datasets)[0] || "";
        setSelectedDataset(first);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Unable to load mortgage industry data.";
        setError(msg);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchIndustryData();
  }, []);

  // Reset table state when dataset changes
  useEffect(() => {
    setFilters({});
    setSort({ column: null, direction: "asc" });
    setPage(1);
  }, [selectedDataset]);

  const onDownloadJson = () => {
    if (!data) return;
    downloadTextFile(
      `residential-mortgage-industry-data.json`,
      JSON.stringify(data, null, 2),
      "application/json;charset=utf-8"
    );
  };

  const statusTag = useMemo(() => {
    if (isLoading) return "Loading…";
    if (error) return "Error";
    const anyDataset = Object.values(datasets)[0];
    if (!anyDataset) return "No data";
    const dates = getUniqueValues(anyDataset.rows, dateColumn);
    const latest = maxDateString(dates);
    return latest ? `Latest: ${formatDateDisplay(latest)}` : "Latest: N/A";
  }, [dateColumn, datasets, error, isLoading]);

  const empty = !isLoading && !error && metrics.length === 0;

  const datasetValueColumn = useMemo(() => {
    if (!currentDataset) return null;
    return pickPrimaryValueColumn(currentDataset, dateColumn);
  }, [currentDataset, dateColumn]);

  const filterableColumns = useMemo(() => {
    if (!currentDataset) return [];
    const valueCols = currentDataset.columns.filter((c) => isNumericColumn(currentDataset, c));
    return currentDataset.columns.filter((c) => c === dateColumn || !valueCols.includes(c));
  }, [currentDataset, dateColumn]);

  const filteredSortedRows = useMemo(() => {
    if (!currentDataset) return [];
    let rows = currentDataset.rows;

    // Filters
    rows = rows.filter((r) => {
      for (const col of filterableColumns) {
        const selected = filters[col];
        if (!selected) continue;
        if (String(r[col] ?? "") !== selected) return false;
      }
      return true;
    });

    // Sort
    if (sort.column) {
      const col = sort.column;
      const dir = sort.direction === "asc" ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const an = toNumber(a[col]);
        const bn = toNumber(b[col]);
        if (an !== null && bn !== null) return (an - bn) * dir;
        const as = String(a[col] ?? "");
        const bs = String(b[col] ?? "");
        return as.localeCompare(bs) * dir;
      });
    }
    return rows;
  }, [currentDataset, filterableColumns, filters, sort.column, sort.direction]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredSortedRows.length / pageSize));
  }, [filteredSortedRows.length, pageSize]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSortedRows.slice(start, start + pageSize);
  }, [filteredSortedRows, page, pageSize]);

  const handleExportCsv = () => {
    if (!currentDataset) return;
    const cols = currentDataset.columns;
    const escape = (v: unknown) => {
      const s = v === null || v === undefined ? "" : String(v);
      const needs = /[",\n]/.test(s);
      return needs ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      cols.map(escape).join(","),
      ...filteredSortedRows.map((r) => cols.map((c) => escape(r[c])).join(",")),
    ].join("\n");
    downloadTextFile(
      `residential-mortgage-${selectedDataset || "dataset"}.csv`,
      lines,
      "text/csv;charset=utf-8"
    );
  };

  const overviewKpis = useMemo(() => {
    if (!data) return [];
    const items = [];
    for (const metric of data.data.metrics || []) {
      const ds = data.data.datasets[metric];
      if (!ds) continue;
      const valueCol = pickPrimaryValueColumn(ds, data.data.dateColumn);
      if (!valueCol) continue;
      const dates = getUniqueValues(ds.rows, data.data.dateColumn);
      const latest = maxDateString(dates);
      if (!latest) continue;
      const total = sumForDate(ds, data.data.dateColumn, valueCol, latest);
      items.push({
        metric,
        valueCol,
        latest,
        total,
      });
    }
    return items;
  }, [data]);

  const approvalsVsApplications = useMemo(() => {
    if (!data) return null;
    const app = data.data.datasets["Applications"];
    const appr = data.data.datasets["Approvals"];
    if (!app || !appr) return null;
    const appCol = pickPrimaryValueColumn(app, data.data.dateColumn);
    const apprCol = pickPrimaryValueColumn(appr, data.data.dateColumn);
    if (!appCol || !apprCol) return null;
    const latestApp = maxDateString(getUniqueValues(app.rows, data.data.dateColumn));
    const latestAppr = maxDateString(getUniqueValues(appr.rows, data.data.dateColumn));
    const latest = [latestApp, latestAppr].filter(Boolean).sort().at(-1) as string | undefined;
    if (!latest) return null;
    const appTotal = sumForDate(app, data.data.dateColumn, appCol, latest);
    const apprTotal = sumForDate(appr, data.data.dateColumn, apprCol, latest);
    const ratio = appTotal > 0 ? (apprTotal / appTotal) * 100 : null;
    return { latest, appTotal, apprTotal, ratio };
  }, [data]);

  const buildMetricSeries = (metricName: string): TimeSeries[] => {
    if (!data) return [];
    const ds = data.data.datasets[metricName];
    if (!ds) return [];
    const valueCol = pickPrimaryValueColumn(ds, data.data.dateColumn);
    if (!valueCol) return [];
    return [
      {
        label: metricName,
        color: CYBER_COLORS[0],
        points: sumByDate(ds, data.data.dateColumn, valueCol),
      },
    ];
  };

  const buildOverlaySeries = (metricNames: string[]): TimeSeries[] => {
    if (!data) return [];
    const out: TimeSeries[] = [];
    metricNames.forEach((name, idx) => {
      const ds = data.data.datasets[name];
      if (!ds) return;
      const valueCol = pickPrimaryValueColumn(ds, data.data.dateColumn);
      if (!valueCol) return;
      out.push({
        label: name,
        color: CYBER_COLORS[idx % CYBER_COLORS.length],
        points: sumByDate(ds, data.data.dateColumn, valueCol),
      });
    });
    return out;
  };

  const TrendsView: React.FC = () => {
    const [overlay, setOverlay] = useState<string[]>(["Applications"]);
    const [groupBy, setGroupBy] = useState<string>("");

    useEffect(() => {
      if (!data) return;
      const first = data.data.metrics?.[0] || "";
      setOverlay((prev) => {
        if (!first) return prev;
        if (!prev.length) return [first];
        // If current overlay is not in available metrics, reset to first.
        if (!data.data.metrics.includes(prev[0])) return [first];
        return prev;
      });
    }, [data]);

    const availableGroupBys = useMemo(() => {
      if (!data) return [];
      // group-by only if the selected primary dataset has those columns
      const primary = data.data.datasets[overlay[0]];
      if (!primary) return [];
      const cols = primary.columns;
      const candidates = ["Lender Type", "Insurance Status", "Mortgage Increase Type", "Mortgage Decrease Type"];
      return candidates.filter((c) => cols.includes(c));
    }, [data, overlay]);

    const series = useMemo(() => {
      if (!data) return [];
      if (!overlay.length) return [];

      // If grouping, only chart the first metric to keep readable
      if (groupBy) {
        const metric = overlay[0];
        const ds = data.data.datasets[metric];
        if (!ds) return [];
        const valueCol = pickPrimaryValueColumn(ds, data.data.dateColumn);
        if (!valueCol) return [];
        const groups = getUniqueValues(ds.rows, groupBy);
        // Limit number of lines to avoid noise: top 6 by latest date
        const dates = getUniqueValues(ds.rows, data.data.dateColumn);
        const latest = maxDateString(dates);
        const ranked = groups
          .map((g) => ({
            g,
            v:
              latest
                ? ds.rows.reduce((acc, r) => {
                    if (String(r[groupBy]) !== g) return acc;
                    if (String(r[data.data.dateColumn]) !== latest) return acc;
                    const n = toNumber(r[valueCol]);
                    return n === null ? acc : acc + n;
                  }, 0)
                : 0,
          }))
          .sort((a, b) => b.v - a.v)
          .slice(0, 6)
          .map((x) => x.g);

        return ranked.map((g, idx) => ({
          label: g,
          color: CYBER_COLORS[idx % CYBER_COLORS.length],
          points: sumByDate(ds, data.data.dateColumn, valueCol, (r) => String(r[groupBy]) === g),
        }));
      }

      return buildOverlaySeries(overlay);
    }, [buildOverlaySeries, data, groupBy, overlay]);

    return (
      <div className="flex flex-col gap-4">
        <div className={`${panelClass} flex items-center justify-between gap-3 flex-wrap`}>
          <div className="flex flex-col gap-0.5">
            <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
              Chart controls
            </div>
            <div className="text-[11px] text-[#8b92b0]">
              Overlay metrics, or group a single metric by a dimension column.
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex flex-col gap-1 min-w-[240px]">
              <span className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0]">
                Overlay metrics
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                {metrics.map((m) => {
                  const active = overlay.includes(m);
                  return (
                    <button
                      key={m}
                      onClick={() => {
                        setGroupBy("");
                        setOverlay((prev) => {
                          const next = prev.includes(m)
                            ? prev.filter((x) => x !== m)
                            : [...prev, m].slice(0, 4);
                          return next.length ? next : [m];
                        });
                      }}
                      className={`px-2.25 py-1 rounded-full border cursor-pointer text-[11px] transition-all duration-200 ${
                        active
                          ? "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]"
                          : "border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[#8b92b0] hover:text-white"
                      }`}
                      title="Max 4 overlays"
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </label>

            <Select
              label="Group by (optional)"
              value={groupBy}
              options={availableGroupBys}
              onChange={(v) => setGroupBy(v)}
            />
          </div>
        </div>

        <LineChart
          title={groupBy ? `${overlay[0]} · grouped by ${groupBy}` : "Metric trends"}
          subtitle={
            groupBy
              ? `Each line is a ${groupBy} segment.`
              : "Each line is a metric total by reporting date."
          }
          series={series}
        />
      </div>
    );
  };

  const ComparisonsView: React.FC = () => {
    const appApprSeries = useMemo(() => {
      return buildOverlaySeries(["Applications", "Approvals"].filter((m) => datasets[m]));
    }, [datasets, buildOverlaySeries]);

    const decreasesOriginatedSeries = useMemo(() => {
      const left = datasets["Mortgage Decreases"] ? "Mortgage Decreases" : "";
      const right = datasets["Total Loan Value Originated"]
        ? "Total Loan Value Originated"
        : datasets["Number of Loans Originated"]
        ? "Number of Loans Originated"
        : "";
      const names = [left, right].filter(Boolean);
      return buildOverlaySeries(names);
    }, [datasets, buildOverlaySeries]);

    const computeLastDelta = (series: TimeSeries[]) => {
      if (series.length < 2) return null;
      const a = series[0].points.slice().sort((x, y) => x.date.localeCompare(y.date));
      const b = series[1].points.slice().sort((x, y) => x.date.localeCompare(y.date));
      const lastA = a.at(-1);
      const lastB = b.at(-1);
      const prevA = a.at(-2);
      const prevB = b.at(-2);
      if (!lastA || !lastB) return null;
      const delta = lastB.value - lastA.value;
      const deltaPrev =
        prevA && prevB ? prevB.value - prevA.value : null;
      const pct = deltaPrev !== null ? percentChange(delta, deltaPrev) : null;
      return { delta, pct };
    };

    const delta1 = computeLastDelta(appApprSeries);
    const delta2 = computeLastDelta(decreasesOriginatedSeries);

    return (
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-1">
        <div className="flex flex-col gap-3">
          <div className={`${panelClass} flex items-center justify-between gap-2 flex-wrap`}>
            <div className="flex flex-col gap-0.5">
              <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
                Applications vs Approvals
              </div>
              <div className="text-[11px] text-[#8b92b0]">
                Side-by-side trend comparison at the industry level.
              </div>
            </div>
            {delta1 && (
              <div className="flex items-center gap-2">
                <Badge value={`Δ last: ${delta1.delta.toLocaleString("en-US")}`} />
                {delta1.pct !== null && (
                  <Badge
                    value={`${delta1.pct >= 0 ? "+" : ""}${delta1.pct.toFixed(1)}% vs prev`}
                    tone={delta1.pct >= 0 ? "pos" : "neg"}
                  />
                )}
              </div>
            )}
          </div>
          <LineChart
            title="Applications vs Approvals"
            subtitle="Totals by reporting date."
            series={appApprSeries}
          />
        </div>

        <div className="flex flex-col gap-3">
          <div className={`${panelClass} flex items-center justify-between gap-2 flex-wrap`}>
            <div className="flex flex-col gap-0.5">
              <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
                Decreases vs Originations
              </div>
              <div className="text-[11px] text-[#8b92b0]">
                Delta view comparing decreases to originated totals.
              </div>
            </div>
            {delta2 && (
              <div className="flex items-center gap-2">
                <Badge value={`Δ last: ${delta2.delta.toLocaleString("en-US")}`} />
                {delta2.pct !== null && (
                  <Badge
                    value={`${delta2.pct >= 0 ? "+" : ""}${delta2.pct.toFixed(1)}% vs prev`}
                    tone={delta2.pct >= 0 ? "pos" : "neg"}
                  />
                )}
              </div>
            )}
          </div>
          <LineChart
            title="Mortgage Decreases vs Originated"
            subtitle="Totals by reporting date."
            series={decreasesOriginatedSeries}
          />
        </div>
      </div>
    );
  };

  const MethodologyView: React.FC = () => {
    return (
      <article className={panelClass}>
        <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
          <div className="flex flex-col gap-0.5">
            <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
              Methodology / Glossary
            </div>
            <div className="text-[11px] text-[#8b92b0]">
              Definitions, data sources, and notes on aggregation & missing values.
            </div>
          </div>
        </div>

        <div className={`${innerPanelClass} p-4`}>
          <div className="grid grid-cols-2 gap-4 max-lg:grid-cols-1">
            <div className="flex flex-col gap-2">
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0]">
                Metrics
              </div>
              <ul className="text-[13px] text-[#cbd3ff] leading-relaxed list-disc pl-5">
                <li>Applications: count of applications submitted.</li>
                <li>Approvals: count of applications approved.</li>
                <li>Mortgage Decreases: decreases by type (where provided).</li>
                <li>Number of Loans Originated / Outstanding: loan counts.</li>
                <li>Total Loan Value Originated / Outstanding: aggregate balances.</li>
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0]">
                Data sources
              </div>
              <ul className="text-[13px] text-[#cbd3ff] leading-relaxed list-disc pl-5">
                <li>CMHC</li>
                <li>OSFI</li>
                <li>Statistics Canada</li>
              </ul>
              <div className="mt-2 text-[11px] text-[#8b92b0]">
                Missing values may appear where reporting is unavailable or suppressed.
                Aggregations in this UI default to sums across rows for a given reporting date.
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0]">
              Dimensions
            </div>
            <div className="mt-2 text-[13px] text-[#cbd3ff] leading-relaxed">
              Filters are generated dynamically from dataset columns (for example,
              Lender Type, Insurance Status, Mortgage Increase Type, Mortgage Decrease Type).
            </div>
          </div>
        </div>
      </article>
    );
  };

  const AccessView: React.FC = () => {
    const curlExample = `curl -X GET "${IndustryDataService.BASE_URL}/api/industry-data/residential-mortgage" \\\n  -H "X-API-Key: <your key>"`;

    const fetchExample = `fetch("/api/industry-data/residential-mortgage", {\n  headers: { "X-API-Key": "<your key>" }\n}).then(r => r.json()).then(console.log);`;

    const copy = async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        downloadTextFile("api-request.txt", text, "text/plain;charset=utf-8");
      }
    };

    return (
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-1">
        <article className={panelClass}>
          <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
                API access
              </div>
              <div className="text-[11px] text-[#8b92b0]">
                Use an API key via the <code className="font-jetbrains-mono">X-API-Key</code>{" "}
                header.
              </div>
            </div>
          </div>
          <div className={`${innerPanelClass} p-4`}>
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] mb-2">
              Header
            </div>
            <div className="text-[13px] text-[#cbd3ff] font-jetbrains-mono">
              X-API-Key: {"<your key>"}
            </div>
            <div className="mt-4 text-[11px] text-[#8b92b0]">
              Tip: set <code className="font-jetbrains-mono">REACT_APP_INDUSTRY_DATA_API_KEY</code>{" "}
              for local development.
            </div>
          </div>
        </article>

        <article className={panelClass}>
          <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
                Downloads & examples
              </div>
              <div className="text-[11px] text-[#8b92b0]">
                Copy request templates or download the fetched payload.
              </div>
            </div>
          </div>
          <div className={`${innerPanelClass} p-4 flex flex-col gap-3`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0]">
                cURL
              </div>
              <button
                onClick={() => copy(curlExample)}
                className="px-3 py-1.5 rounded-full border border-[rgba(139,146,176,0.5)] bg-[rgba(10,14,39,0.9)] text-[11px] text-[#cbd3ff] hover:bg-[rgba(26,31,58,0.9)]"
              >
                Copy
              </button>
            </div>
            <pre className="text-[12px] text-[#cbd3ff] font-jetbrains-mono whitespace-pre-wrap">
{curlExample}
            </pre>

            <div className="flex items-center justify-between gap-2 mt-2">
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0]">
                Fetch
              </div>
              <button
                onClick={() => copy(fetchExample)}
                className="px-3 py-1.5 rounded-full border border-[rgba(139,146,176,0.5)] bg-[rgba(10,14,39,0.9)] text-[11px] text-[#cbd3ff] hover:bg-[rgba(26,31,58,0.9)]"
              >
                Copy
              </button>
            </div>
            <pre className="text-[12px] text-[#cbd3ff] font-jetbrains-mono whitespace-pre-wrap">
{fetchExample}
            </pre>

            <div className="flex items-center gap-2 flex-wrap pt-1">
              <button
                onClick={onDownloadJson}
                className="px-4 py-2 rounded-full border-none cursor-pointer text-[13px] font-semibold font-inherit bg-gradient-to-r from-cyber-blue to-blockchain-purple text-white shadow-[0_10px_30px_rgba(0,212,255,0.4)] inline-flex items-center gap-2 whitespace-nowrap transition-all duration-100 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_rgba(0,212,255,0.6)] active:translate-y-0 active:shadow-[0_6px_20px_rgba(0,212,255,0.4)]"
                disabled={!data}
              >
                Download fetched JSON
                <span className="text-sm">⇩</span>
              </button>
              <button
                onClick={() => {
                  setView("explorer");
                  // keep dataset selection
                }}
                className="px-3 py-2 rounded-full border border-[rgba(139,146,176,0.5)] bg-[rgba(10,14,39,0.9)] text-[13px] text-[#cbd3ff] hover:bg-[rgba(26,31,58,0.9)]"
              >
                Go to Data Explorer
              </button>
            </div>
          </div>
        </article>
      </div>
    );
  };

  const OverviewView: React.FC = () => {
    return (
      <div className="flex flex-col gap-4">
        <article className={panelClass}>
          <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
                KPI snapshot
              </div>
              <div className="text-[11px] text-[#8b92b0]">
                Latest reporting date totals by metric (sum across dataset rows).
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setView("explorer")}
                className="px-3 py-2 rounded-full border border-[rgba(139,146,176,0.5)] bg-[rgba(10,14,39,0.9)] text-[13px] text-[#cbd3ff] hover:bg-[rgba(26,31,58,0.9)]"
              >
                Open Data Explorer
              </button>
              <button
                onClick={() => setView("trends")}
                className="px-3 py-2 rounded-full border border-[rgba(139,146,176,0.5)] bg-[rgba(10,14,39,0.9)] text-[13px] text-[#cbd3ff] hover:bg-[rgba(26,31,58,0.9)]"
              >
                View Trends
              </button>
            </div>
          </div>

          <div className={`${innerPanelClass} p-4`}>
            {overviewKpis.length === 0 ? (
              <div className="text-[#8b92b0] text-sm">No data available for this dataset.</div>
            ) : (
              <div className="grid grid-cols-3 gap-3 xl:grid-cols-2 lg:grid-cols-1">
                {overviewKpis.map((k, idx) => (
                  <div
                    key={k.metric}
                    className="p-4 rounded-[16px] border border-[rgba(139,146,176,0.25)] bg-[rgba(5,7,22,0.55)]"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs uppercase tracking-[0.14em] text-[#8b92b0]">
                        {k.metric}
                      </div>
                      <span className="text-[11px] px-2.5 py-1 rounded-full border border-[rgba(139,146,176,0.7)] text-[#8b92b0]">
                        {formatDateDisplay(k.latest)}
                      </span>
                    </div>
                    <div className="mt-2 font-jetbrains-mono text-2xl font-semibold bg-gradient-to-r from-white to-cyber-blue bg-clip-text text-transparent">
                      {k.total.toLocaleString("en-US")}
                    </div>
                    <div className="mt-1 text-[11px] text-[#8b92b0] flex items-center justify-between gap-2">
                      <span>Column: {k.valueCol}</span>
                      <span className="inline-flex items-center gap-1.25">
                        <span
                          className="w-2.25 h-2.25 rounded-full"
                          style={{
                            background: CYBER_COLORS[idx % CYBER_COLORS.length],
                          }}
                        />
                        Total
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </article>

        <div className="grid grid-cols-2 gap-4 xl:grid-cols-1">
          <article className={panelClass}>
            <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
              <div className="flex flex-col gap-0.5">
                <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
                  Quick highlights
                </div>
                <div className="text-[11px] text-[#8b92b0]">
                  Fast comparisons based on the latest reporting date.
                </div>
              </div>
            </div>
            <div className={`${innerPanelClass} p-4`}>
              {approvalsVsApplications ? (
                <div className="flex flex-col gap-2">
                  <div className="text-[11px] text-[#8b92b0]">
                    Latest date:{" "}
                    <strong>{formatDateDisplay(approvalsVsApplications.latest)}</strong>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      value={`Applications: ${approvalsVsApplications.appTotal.toLocaleString("en-US")}`}
                    />
                    <Badge
                      value={`Approvals: ${approvalsVsApplications.apprTotal.toLocaleString("en-US")}`}
                    />
                    {approvalsVsApplications.ratio !== null && (
                      <Badge
                        value={`Approval rate: ${approvalsVsApplications.ratio.toFixed(1)}%`}
                        tone="pos"
                      />
                    )}
                  </div>
                  <div className="mt-2 text-[11px] text-[#8b92b0]">
                    Tip: use Comparisons to see deltas and percent changes over time.
                  </div>
                </div>
              ) : (
                <div className="text-[#8b92b0] text-sm">
                  Highlight requires “Applications” and “Approvals” datasets.
                </div>
              )}
            </div>
          </article>

          <LineChart
            title={selectedDataset ? `${selectedDataset} trend` : "Trend"}
            subtitle="Totals by reporting date."
            series={selectedDataset ? buildMetricSeries(selectedDataset) : []}
          />
        </div>
      </div>
    );
  };

  const ExplorerView: React.FC = () => {
    return (
      <div className="flex flex-col gap-4">
        <article className={panelClass}>
          <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
                Data explorer
              </div>
              <div className="text-[11px] text-[#8b92b0]">
                Select a dataset, apply filters, sort columns, paginate, and export to CSV.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex flex-col gap-1 min-w-[260px]">
                <span className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0]">
                  Dataset
                </span>
                <select
                  className="px-3 py-2 rounded-full border border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[12px] text-white outline-none"
                  value={selectedDataset}
                  onChange={(e) => setSelectedDataset(e.target.value)}
                >
                  {metrics.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>

              <button
                onClick={handleExportCsv}
                className="px-4 py-2 rounded-full border-none cursor-pointer text-[13px] font-semibold font-inherit bg-gradient-to-r from-cyber-blue to-blockchain-purple text-white shadow-[0_10px_30px_rgba(0,212,255,0.4)] inline-flex items-center gap-2 whitespace-nowrap transition-all duration-100 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_rgba(0,212,255,0.6)] active:translate-y-0 active:shadow-[0_6px_20px_rgba(0,212,255,0.4)]"
                disabled={!currentDataset}
              >
                Export CSV
                <span className="text-sm">⇩</span>
              </button>
            </div>
          </div>

          <div className={`${innerPanelClass} p-4`}>
            {!currentDataset ? (
              <div className="text-[#8b92b0] text-sm">
                No data available for this dataset.
              </div>
            ) : (
              <div className="flex items-end justify-between gap-3 flex-wrap">
                <div className="flex items-end gap-3 flex-wrap">
                  {filterableColumns.map((col) => (
                    <Select
                      key={col}
                      label={col}
                      value={filters[col] || ""}
                      options={getUniqueValues(currentDataset.rows, col)}
                      onChange={(v) =>
                        setFilters((prev) => ({
                          ...prev,
                          [col]: v,
                        }))
                      }
                    />
                  ))}
                </div>
                <label className="flex flex-col gap-1 min-w-[140px]">
                  <span className="text-[11px] uppercase tracking-[0.16em] text-[#8b92b0]">
                    Page size
                  </span>
                  <select
                    className="px-3 py-2 rounded-full border border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[12px] text-white outline-none"
                    value={String(pageSize)}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {[25, 50, 100].map((s) => (
                      <option key={s} value={String(s)}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
        </article>

        <article className={panelClass}>
          <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
                Table view
              </div>
              <div className="text-[11px] text-[#8b92b0]">
                Click headers to sort. Pagination applies to filtered results.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#8b92b0]">
              <span>
                Rows: <strong>{filteredSortedRows.length.toLocaleString("en-US")}</strong>
              </span>
              {datasetValueColumn && (
                <span>
                  Primary value: <strong>{datasetValueColumn}</strong>
                </span>
              )}
            </div>
          </div>

          {!currentDataset ? (
            <div className={`${innerPanelClass} p-4`}>
              <div className="text-[#8b92b0] text-sm">No data available for this dataset.</div>
            </div>
          ) : (
            <>
              <div className="rounded-xl overflow-auto border border-[rgba(139,146,176,0.35)] bg-gradient-to-b from-[#1c2243] to-[#050716] max-h-[520px]">
                <table className="w-full border-collapse text-xs">
                  <thead className="sticky top-0 z-[1] bg-[rgba(5,7,22,0.95)] backdrop-blur-sm">
                    <tr>
                      {currentDataset.columns.map((header) => {
                        const active = sort.column === header;
                        return (
                          <th
                            key={header}
                            onClick={() =>
                              setSort((prev) => {
                                if (prev.column !== header) {
                                  return { column: header, direction: "asc" };
                                }
                                return {
                                  column: header,
                                  direction: prev.direction === "asc" ? "desc" : "asc",
                                };
                              })
                            }
                            className="px-2.5 py-2.25 text-left border-b border-[rgba(139,146,176,0.14)] text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] whitespace-nowrap cursor-pointer select-none"
                            title="Sort"
                          >
                            <span className="inline-flex items-center gap-2">
                              {header}
                              {active && (
                                <span className="text-cyber-blue">
                                  {sort.direction === "asc" ? "↑" : "↓"}
                                </span>
                              )}
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={currentDataset.columns.length}
                          className="px-3 py-10 text-center text-[#8b92b0] text-sm"
                        >
                          No data available for this dataset.
                        </td>
                      </tr>
                    ) : (
                      pagedRows.map((row, idx) => (
                        <tr
                          key={idx}
                          className="hover:bg-[rgba(0,212,255,0.08)] transition-colors"
                        >
                          {currentDataset.columns.map((c) => (
                            <td
                              key={c}
                              className="px-2.5 py-2.25 border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap"
                            >
                              {c === dateColumn && typeof row[c] === "string"
                                ? formatDateDisplay(String(row[c]))
                                : String(row[c] ?? "")}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-2 flex items-center justify-between gap-2 flex-wrap text-[11px] text-[#8b92b0]">
                <span>
                  Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 rounded-full border border-[rgba(139,146,176,0.5)] bg-[rgba(10,14,39,0.9)] text-[11px] text-[#cbd3ff] disabled:opacity-40 hover:bg-[rgba(26,31,58,0.9)]"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="px-3 py-1.5 rounded-full border border-[rgba(139,146,176,0.5)] bg-[rgba(10,14,39,0.9)] text-[11px] text-[#cbd3ff] disabled:opacity-40 hover:bg-[rgba(26,31,58,0.9)]"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </article>
      </div>
    );
  };

  const content = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-1">
          <div className={panelClass}>
            <SkeletonBlock className="h-5 w-40 mb-3" />
            <SkeletonBlock className="h-3 w-72 mb-5" />
            <SkeletonBlock className="h-36 w-full" />
          </div>
          <div className={panelClass}>
            <SkeletonBlock className="h-5 w-44 mb-3" />
            <SkeletonBlock className="h-3 w-64 mb-5" />
            <SkeletonBlock className="h-36 w-full" />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <article className={panelClass}>
          <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
                Unable to load mortgage industry data.
              </div>
              <div className="text-[11px] text-[#8b92b0]">
                Check your API key and network connectivity.
              </div>
            </div>
            <Badge value="Error" tone="neg" />
          </div>
          <div className={`${innerPanelClass} p-4`}>
            <div className="text-[12px] text-[#cbd3ff] font-jetbrains-mono whitespace-pre-wrap">
              {error}
            </div>
          </div>
        </article>
      );
    }

    if (empty) {
      return (
        <article className={panelClass}>
          <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
                No data available for this dataset.
              </div>
              <div className="text-[11px] text-[#8b92b0]">
                The API returned an empty metrics/datasets payload.
              </div>
            </div>
          </div>
        </article>
      );
    }

    if (view === "overview") return <OverviewView />;
    if (view === "explorer") return <ExplorerView />;
    if (view === "trends") return <TrendsView />;
    if (view === "comparisons") return <ComparisonsView />;
    if (view === "methodology") return <MethodologyView />;
    return <AccessView />;
  };

  return (
    <div className="p-6 pb-8 flex flex-col gap-6">
      <IndustryDataTopbar
        onSidebarToggle={onSidebarToggle}
        statusTag={statusTag}
        onReload={() => {
          setIsLoading(true);
          setError(null);
          IndustryDataService.getResidentialMortgageIndustryData()
            .then((res) => {
              setData(res);
              const first =
                res.data.metrics?.[0] ||
                Object.keys(res.data.datasets)[0] ||
                "";
              setSelectedDataset((prev) => prev || first);
            })
            .catch((e) => {
              const msg =
                e instanceof Error
                  ? e.message
                  : "Unable to load mortgage industry data.";
              setError(msg);
              setData(null);
            })
            .finally(() => setIsLoading(false));
        }}
        onDownloadJson={onDownloadJson}
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <PillTabs tabs={tabs} active={view} onChange={setView} />
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#8b92b0]">
          <span>
            Datasets: <strong>{metrics.length}</strong>
          </span>
          {selectedDataset && (
            <span>
              Active: <strong>{selectedDataset}</strong>
            </span>
          )}
        </div>
      </div>

      {content()}
    </div>
  );
};

